import { ImgID } from "../../image_loader/img_id.js";
import { offerButtonLabel } from "../../offers/offer_visual_registry.js";
import { formatMinutesShort } from "../../common/reward_format.js";
import type { OfferDto, OfferRewardDto } from "../../api/dto/offers.dto.js";
import { OfferService } from "../../offers/offer_service.js";
import { TonPaymentPending } from "../../plugins/ton_payments.js";
import { adoptEconomyFromPlayer, refreshPlayerFromServer } from "../../api/player_sync.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";

/**
 * Generic, config-driven trigger offer view. Content is intentionally limited
 * to text and known reward icons: LiveOps can launch a new offer without a
 * client update, while bespoke art/layouts can replace it later.
 */
export class TriggerOfferDialog extends AbstractDialog
{
  private purchase_in_progress = false;
  private price_text?: UITextElement;
  private close_tracked = false;

  /** Analytics/authorization context: which entry point showed this and, if a trigger did, its decision. */
  private readonly placement_id: string;
  private readonly trigger_event_id: string | undefined;

  constructor(
    private readonly offer: OfferDto,
    context: { placement_id: string; trigger_event_id?: string },
  )
  {
    const width = LayoutScaler.widthFromDesign( 826 );
    const height = LayoutScaler.heightFromDesign( 970 );
    super(
      DialogManager.ctx,
      ( DialogManager.getWidth() - width ) / 2,
      ( DialogManager.getHeight() - height ) / 2,
      width,
      height,
      false,
    );
    this.placement_id = context.placement_id;
    this.trigger_event_id = context.trigger_event_id;

    this.createBackground();
    this.createContent();
    this.createCloseButton();
    void this.loadPrice();
  }

  public name(): string { return 'TriggerOfferDialog'; }

  public render(): void
  {
    this.startRender();
    this.finishRender();
  }

  public override onDialogCloseRequested( reason: string ): void
  {
    if ( this.close_tracked ) return;
    this.close_tracked = true;
    void OfferService.trackClosed( this.offer.id, {
      placement: this.placement_id, placement_id: this.placement_id, template: 'rewards_dialog',
      trigger_event_id: this.trigger_event_id,
      reason,
    } );
  }

  private createBackground(): void
  {
    this.addChild( new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      LayoutScaler.cornerRadiusFromDesign( 62 ),
      {
        type: 'linear-gradient',
        direction: 'vertical',
        stops: [
          { offset: 0, color: '#3A8FFF' },
          { offset: 0.45, color: '#1464CF' },
          { offset: 1, color: '#073985' },
        ],
      },
    ) );
  }

  private createContent(): void
  {
    const title = this.offer.data?.title;
    const description = this.offer.data?.description;
    // `texts` is the configured override; the older data.*_key spelling is
    // kept as a fallback for catalogs saved before it existed.
    const title_key = this.offer.texts?.title_key
      ?? ( typeof this.offer.data?.title_key === 'string' ? this.offer.data.title_key : undefined );
    const description_key = this.offer.texts?.description_key
      ?? ( typeof this.offer.data?.description_key === 'string' ? this.offer.data.description_key : undefined );

    const heading = new UITextElement( this.ctx, LayoutScaler.xFromDesign( 56 ), LayoutScaler.yFromDesign( 56 ),
      this.rect.getW() - LayoutScaler.widthFromDesign( 112 ), LayoutScaler.heightFromDesign( 135 ), {
        ...( title_key ? { localization_key: title_key } : { text: typeof title === 'string' ? title : 'SPECIAL OFFER' } ),
        font_size: LayoutScaler.textSizeFromDesign( 74 ), font_weight: 900,
        text_style: TextStyleScheme.rubik, text_color: '#FFFFFF',
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ), outline_color: '#073985',
        text_align: MyTextAlign.Center, vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true, scale_to_width: true, min_font_size: LayoutScaler.textSizeFromDesign( 42 ),
      } );
    this.addChild( heading );

    const body = new UITextElement( this.ctx, LayoutScaler.xFromDesign( 76 ), LayoutScaler.yFromDesign( 182 ),
      this.rect.getW() - LayoutScaler.widthFromDesign( 152 ), LayoutScaler.heightFromDesign( 110 ), {
        ...( description_key ? { localization_key: description_key } : { text: typeof description === 'string' ? description : 'Limited-time reward pack' } ),
        font_size: LayoutScaler.textSizeFromDesign( 40 ), font_weight: 800,
        text_style: TextStyleScheme.rubik, text_color: '#FFFFFF',
        text_align: MyTextAlign.Center, vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
      } );
    this.addChild( body );

    const rewards = this.getRewards();
    rewards.slice( 0, 4 ).forEach( ( reward, index ) => this.addReward( reward, index, Math.min( rewards.length, 4 ) ) );

    const button_w = LayoutScaler.widthFromDesign( 700 );
    const button_h = LayoutScaler.heightFromDesign( 280 );
    const button = new UIImage( this.ctx,
      ( this.rect.getW() - button_w ) / 2, LayoutScaler.yFromDesign( 695.5 ), button_w, button_h,
      ImgID.UI_GreenFrameBtn, undefined, true );
    const price = new UITextElement( this.ctx, 0, 0, button_w, button_h, {
      text: 'BUY', font_size: LayoutScaler.textSizeFromDesign( 62 ), font_weight: 900,
      text_style: TextStyleScheme.rubik, text_color: '#FFFFFF', text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center, ...TextStyleScheme.buttonShadow(),
      outline_color: ColorScheme.menu_button_outline, outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
    } );
    button.addChild( price, true );
    button.setOnClick( () => void this.purchase() );
    this.addChild( button );
    this.price_text = price;
  }

  private getRewards(): OfferRewardDto[]
  {
    const raw = this.offer.rewards ?? this.offer.data?.rewards;
    if ( Array.isArray( raw ) ) return raw.filter( ( value ): value is OfferRewardDto =>
      Boolean( value ) && typeof value === 'object' && typeof ( value as OfferRewardDto ).type === 'string'
    );
    if ( !raw || typeof raw !== 'object' ) return [];
    return Object.entries( raw ).flatMap( ( [ type, amount ] ) => {
      // if ( type === 'no_ads' && amount === true ) return [{ type: 'no_ads' as const, amount: 1 }];
      return typeof amount === 'number' && amount > 0 ? [{ type: type as OfferRewardDto['type'], amount }] : [];
    } );
  }

  private addReward( reward: OfferRewardDto, index: number, total: number ): void
  {
    const size = LayoutScaler.widthFromDesign( 145 );
    const gap = LayoutScaler.widthFromDesign( 24 );
    const row_w = total * size + Math.max( 0, total - 1 ) * gap;
    const x = ( this.rect.getW() - row_w ) / 2 + index * ( size + gap );
    const y = LayoutScaler.yFromDesign( 360 );
    const icon = new UIImage( this.ctx, x, y, size, size, this.rewardIcon( reward.type ) );
    this.addChild( icon );
    this.addChild( new UITextElement( this.ctx, x - LayoutScaler.xFromDesign( 20 ), y + size - LayoutScaler.yFromDesign( 18 ),
      size + LayoutScaler.widthFromDesign( 40 ), LayoutScaler.heightFromDesign( 62 ), {
        text: this.rewardLabel( reward ), font_size: LayoutScaler.textSizeFromDesign( 38 ), font_weight: 900,
        text_style: TextStyleScheme.rubik, text_color: '#FFFFFF', text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center, outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ), outline_color: '#073985',
        auto_wrap: false, scale_to_width: true, min_font_size: LayoutScaler.textSizeFromDesign( 24 ),
      } ) );
  }

  private rewardIcon( type: OfferRewardDto['type'] ): ImgID
  {
    switch ( type )
    {
      case 'coins': return ImgID.IconCoin;
      case 'hearts': case 'infinite_hearts_minutes': return ImgID.UI_ShopInfiniteHeart;
      case 'big_balls': return ImgID.UI_GameStartBigBallBooster;
      case 'infinite_balls': return ImgID.UI_GameStartInfiniteBallsBooster;
      case 'multi_balls': return ImgID.UI_GameStartMultiballsBooster;
      case 'fireworks': return ImgID.UI_GameStartFireworkBooster;
      case 'level_shots': return ImgID.UI_DailyTasksTaskCannon;
      // case 'no_ads': case 'no_ads_minutes': return ImgID.UI_RemoveAdsIcon;
    }
  }

  private rewardLabel( reward: OfferRewardDto ): string
  {
    if ( reward.type === 'infinite_hearts_minutes' )
    {
      const hours = reward.amount / 60;
      return hours % 1 === 0 ? `${hours}h` : `${reward.amount}m`;
    }
    // if ( reward.type === 'no_ads_minutes' ) return formatMinutesShort( reward.amount );
    if ( reward.type === 'level_shots' ) return `+${reward.amount}`;
    // return reward.type === 'no_ads' ? 'NO ADS' : `×${reward.amount}`;
    return `×${reward.amount}`;
  }

  private async loadPrice(): Promise<void>
  {
    if ( OfferService.isFreeOffer( this.offer.id ) || OfferService.isRewardedOffer( this.offer.id ) )
    {
      this.setPrice( offerButtonLabel( this.offer, OfferService.isRewardedOffer( this.offer.id ) ? 'FREE' : 'CLAIM' ) );
      return;
    }
    const product_id = OfferService.getPlatformProductId( this.offer.id );
    if ( !product_id ) { this.setPrice( 'N/A' ); return; }
    try
    {
      const details = await ( await import( '../../plugins/iap_plugin.js' ) ).IapPlugin.getProductDetails( product_id );
      this.setPrice( details?.formatted_price ?? offerButtonLabel( this.offer, 'BUY' ) );
      const price_context = {
        placement: this.placement_id, placement_id: this.placement_id, template: 'rewards_dialog', trigger_event_id: this.trigger_event_id, productid: product_id,
        formatted_price: details?.formatted_price, currency: details?.currency, price_micros: details?.price_micros,
      };
      if ( details?.formatted_price ) await OfferService.trackPriceLoaded( this.offer.id, price_context );
      else await OfferService.trackPriceUnavailable( this.offer.id, { ...price_context, failure_reason: 'product_details_missing' } );
    }
    catch
    {
      this.setPrice( offerButtonLabel( this.offer, 'BUY' ) );
      await OfferService.trackPriceUnavailable( this.offer.id, { placement: this.placement_id, placement_id: this.placement_id, template: 'rewards_dialog', trigger_event_id: this.trigger_event_id, productid: product_id } );
    }
  }

  private setPrice( text: string ): void
  {
    this.price_text?.setText( text );
    this.invalidateSubtree();
  }

  private async purchase(): Promise<void>
  {
    if ( this.purchase_in_progress ) return;
    this.purchase_in_progress = true;
    this.setPrice( '...' );
    try
    {
      await OfferService.trackClicked( this.offer.id, { placement: this.placement_id, placement_id: this.placement_id, template: 'rewards_dialog', trigger_event_id: this.trigger_event_id } );
      const context = { placement: this.placement_id, placement_id: this.placement_id, template: 'rewards_dialog', trigger_event_id: this.trigger_event_id };
      let result;
      if ( OfferService.isRewardedOffer( this.offer.id ) )
      {
        const watched = await ( await import( '../../ads/rewarded.js' ) ).showRewarded( 'rewards_dialog' );
        await OfferService.trackRewardedAdOutcome( this.offer.id, watched, context );
        if ( !watched.rewarded )
        {
          this.setPrice( offerButtonLabel( this.offer, 'FREE' ) );
          return;
        }
        result = await OfferService.claimFreeOffer( this.offer.id, { ...context, rewarded: true, rewarded_ad_id: watched.ad_id }, { ad_id: watched.ad_id } );
      }
      else
      {
        result = OfferService.isFreeOffer( this.offer.id )
          ? await OfferService.claimFreeOffer( this.offer.id, context )
          : await OfferService.purchaseOffer( this.offer.id, context );
      }
      if ( result.player ) adoptEconomyFromPlayer( result.player );
      else await refreshPlayerFromServer();
      DialogManager.closeDialog( 'purchase_success' );
    }
    catch ( error )
    {
      // A crypto payment still confirming on-chain is not a failure: the background tracker grants it
      // and refreshes the player; the sheet already told the player to go back to the game.
      if ( error instanceof TonPaymentPending ) { DialogManager.closeDialog( 'purchase_pending' ); return; }
      console.warn( '[TriggerOffer] purchase failed', error );
      this.setPrice( offerButtonLabel( this.offer, OfferService.isFreeOffer( this.offer.id ) ? 'CLAIM' : 'BUY' ) );
    }
    finally { this.purchase_in_progress = false; }
  }

  private createCloseButton(): void
  {
    const size = LayoutScaler.widthFromDesign( 108 );
    const button = new UIImage( this.ctx, this.rect.getW() - size + LayoutScaler.xFromDesign( 16 ),
      LayoutScaler.yFromDesign( -20 ), size, size, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    button.setOnClick( () => DialogManager.closeDialog( 'dismissed' ) );
    this.addChild( button );
  }
}
